import { useCallback, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import { concat, differenceBy } from 'lodash';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/ContentPages/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES } from 'pages/ContentPages/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import LoadingIndicator from 'components/LoadingIndicator';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import { convertKeysToSnakeCase, transformPosition, transformRemoved } from 'utils/helpers';
import DndWrapper from 'components/DndWrapper';
import ContentComponentsForm from 'components/Content/ContentComponentsForm';
import AddButton from 'components/AddButton';
import { useGetContentPage } from 'api/contentPages';
import { parseContentComponents } from 'components/Content/constants';
import FormWrapper from 'components/FormWrapper';

function ContentPageEdit() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  let debounce: ReturnType<typeof setTimeout>;

  const methods = useForm<ContentPage>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, reset, control } = methods;

  const {
    fields: containers,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'contentContainersAttributes',
    keyName: 'fieldId',
    shouldUnregister: true,
  });

  const addContainerHandler = useCallback(() => {
    append({ kind: '' } as ContentContainer);
  }, [append]);

  const reorderContainers = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      clearTimeout(debounce);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      debounce = setTimeout(() => move(dragIndex, hoverIndex), 500);
    },
    [move],
  );

  const { data: { data: contentPage } = {}, isFetching } = useGetContentPage(+id);

  const { mutate: editContentPage, isLoading: isEditing } = useMutation({
    mutationKey: 'contentPagesMutation',
    mutationFn: (data: ContentPage) => {
      const removedContainers = differenceBy<ContentContainer, ContentContainer>(
        contentPage?.contentContainers,
        data?.contentContainersAttributes,
        'id',
      );

      data.contentContainersAttributes = concat<ContentContainer>(
        ...transformPosition(data?.contentContainersAttributes),
        ...transformRemoved(removedContainers),
      );

      const snakePayload = convertKeysToSnakeCase(data);
      const formData = serialize(snakePayload, SERIALIZER_OPTIONS, new FormData(), 'content_page');
      return API.put(APIRoutes.contentPages.byId(+id), formData);
    },
    onSuccess: () => {
      toast.success('Zmiany w stronie zostały zastosowane');
      navigate(ROUTES.contentPages.base);
      void queryClient.invalidateQueries('contentPages');
    },
    onError: ({ errors }) => {
      setFormErrors(setError, errors);
      toast.error('Wystąpił problem podczas edycji strony');
    },
  });

  const onSubmit = handleSubmit((data: ContentPage) => editContentPage(data));

  useEffect(() => {
    if (contentPage) {
      reset({ ...DEFAULT_VALUES, ...parseContentComponents(contentPage) });
    }
  }, [contentPage, reset]);

  const isLoading = isFetching || isEditing;

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'Kontent',
          to: ROUTES.contentPages.base,
        }}
        crumbs={[{ to: '', label: 'Edytuj stronę' }]}
      />
      {isFetching && <LoadingIndicator />}
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="content-page-editor" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
            {containers.map((container, index) => (
              <DndWrapper
                key={`content-page-editor-${container.fieldId}`}
                id={container.fieldId}
                isLoading={isLoading}
                index={index}
                reorderContainers={reorderContainers}
                removeContainer={remove}
                ContainerWrapper={ContentComponentsForm}
                multipleContainers
              />
            ))}
            <AddButton addHandler={addContainerHandler} mt={2} />
          </Box>
          <ComponentPreview kind="content_page" isEditMode />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} formId="content-page-editor" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default ContentPageEdit;
